import { useMemo } from 'react'

import { EuiFormRow } from '@equipmentshare/ds2'

import ComboBox from 'components/ComboBox/comboBox'

import { useResourcesQuery, Resource } from '../../../../../graphql'
import { appendResourceLabels, buildLabel } from '../../groups/helpers/resource-helper'

export type ResourcePickerProps = {
	id?: string,
	disabled?: boolean,
	label?: string,
	onChange: ( id?: number, option?: Resource ) => void,
	selectedResource?: Resource,
	required?: boolean,
	className?: string,
}

export type ResourceWithEntity = Omit<Resource, 'entity'> & { entity: NonNullable<Resource['entity']> };
export function isResourceWithEntity( resource: Resource | ResourceWithEntity ): resource is ResourceWithEntity {
	return resource.entity !== null && resource.entity !== undefined
}

export default function ResourcePicker( { label, selectedResource, onChange, ...props }: ResourcePickerProps ) {
	const { data, loading } = useResourcesQuery()
	const resources = ( data?.resources ?? [] ) as Resource[]
	const resourcesWithEntities = resources.filter( ( r ) => r.entity !== null )
	const labeledResources = useMemo( () => appendResourceLabels( resourcesWithEntities ), [ resourcesWithEntities ] )

	const selectedWithLabel = useMemo( () => {
		if ( !selectedResource || !isResourceWithEntity( selectedResource ) ) {
			return undefined
		}
		return {
			...selectedResource,
			label: buildLabel( selectedResource )
		}
	}, [ selectedResource ] )

	const handleChange = ( r?: Resource ) => onChange( r?.id, r )

	return (
		<EuiFormRow label={label ?? 'Resource'}>
			<ComboBox
				isLoading={loading}
				setSelectedOption={handleChange}
				selectedOption={selectedWithLabel}
				data={labeledResources}
				getName={( r ) => r.label}
				async={false}
				{...props}
			/>
		</EuiFormRow>
	)
}
